/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Switch,
    FormControlLabel,
    CircularProgress,
    Divider,
    Chip,
    Stack,
} from "@mui/material";
import {
    LinkOutlined,
    CheckCircleOutlined,
    ErrorOutline,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchActiveBusiness,
    fetchQuickBooksStatus,
    updateBusinessSettings,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusiness,
    selectActiveBusinessId,
    selectActiveBusinessRole,
    selectQuickBooks,
} from "../../redux/selectors/collectSelector";
import { colors } from "../../utility/types/constant";
import { IAutoReplySettings, IRecommendationSettings } from "../../utility/types/collectTypes";
import CollectSmsProvisioning from "./CollectSmsProvisioning";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectPrimaryButton,
    CollectGhostButton,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectSettings — workspace settings page.
//
// Sections:
//   • Workspace        — name + timezone
//   • Reminders        — email/SMS toggles, kill switch, manual approval
//   • AI tone          — default tone for AI-generated reminders
//   • QuickBooks       — connection status link
//
// Owner/admin only (PUT /collect/business/settings already enforces role).
// ─────────────────────────────────────────────────────────────────────────

// Common timezone options. Anyone needing something exotic can request it.
const TIMEZONES: { value: string; label: string }[] = [
    { value: "America/New_York", label: "Eastern (New York)" },
    { value: "America/Chicago", label: "Central (Chicago)" },
    { value: "America/Denver", label: "Mountain (Denver)" },
    { value: "America/Phoenix", label: "Mountain (Phoenix, no DST)" },
    { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
    { value: "America/Anchorage", label: "Alaska (Anchorage)" },
    { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
    { value: "America/Toronto", label: "Toronto" },
    { value: "America/Mexico_City", label: "Mexico City" },
    { value: "Europe/London", label: "London" },
    { value: "Europe/Paris", label: "Paris / Berlin" },
    { value: "Australia/Sydney", label: "Sydney" },
    { value: "UTC", label: "UTC" },
];

const AI_TONES: { value: "friendly" | "professional" | "firm"; label: string; hint: string }[] = [
    { value: "friendly", label: "Friendly", hint: "Warm, conversational" },
    { value: "professional", label: "Professional", hint: "Polished, neutral" },
    { value: "firm", label: "Firm", hint: "Direct, urgent" },
];

// Per-recommendation-type auto-apply toggles (Stage 2I). Key maps 1:1 to
// settings.recommendations.autoApply.* on the server.
const RECOMMENDATION_AUTO_APPLY_OPTIONS: {
    key:
    | "reduceOutreach"
    | "enableEarlyReminder"
    | "offerPaymentPlan"
    | "switchChannel"
    | "escalateReview"
    | "pauseAndReview";
    title: string;
    hint: string;
}[] = [
        {
            key: "reduceOutreach",
            title: "Reduce outreach (reliable payers)",
            hint: "Auto-create a task to lighten the reminder cadence for customers who consistently pay on time.",
        },
        {
            key: "enableEarlyReminder",
            title: "Early reminder (occasionally late)",
            hint: "Auto-create a task to add an earlier pre-due nudge for customers who tend to slip a little.",
        },
        {
            key: "offerPaymentPlan",
            title: "Offer a payment plan (at risk)",
            hint: "Auto-create a task to reach out with a payment plan for accounts showing early trouble.",
        },
        {
            key: "switchChannel",
            title: "Switch channel (unresponsive)",
            hint: "Auto-create a task to try SMS or a call when email outreach gets no reply.",
        },
        {
            key: "escalateReview",
            title: "Escalate for review (delinquent)",
            hint: "Auto-create a task to review seriously behind accounts for final notice or collections.",
        },
        {
            key: "pauseAndReview",
            title: "Pause & review (disputes)",
            hint: "Auto-create a task to pause dunning and resolve a dispute before any further collection.",
        },
    ];

const CollectSettings: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();

    const businessId = useSelector(selectActiveBusinessId);
    const business = useSelector(selectActiveBusiness);
    const role = useSelector(selectActiveBusinessRole);
    const qb = useSelector(selectQuickBooks);

    // ── Local form state (rehydrated from server-side `business` on load) ──
    const [name, setName] = useState("");
    const [timezone, setTimezone] = useState("America/Chicago");
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [autoSendEnabled, setAutoSendEnabled] = useState(true);
    const [manualApprovalRequired, setManualApprovalRequired] = useState(false);
    const [defaultAiTone, setDefaultAiTone] = useState<
        "friendly" | "professional" | "firm"
    >("professional");
    // ── Recovery & escalation policy (Stage 2D) ──
    const [promiseGraceDays, setPromiseGraceDays] = useState(1);
    const [brokenPromiseThreshold, setBrokenPromiseThreshold] = useState(2);
    const [escalationDaysPerStage, setEscalationDaysPerStage] = useState(7);
    const [manualReviewBalanceThreshold, setManualReviewBalanceThreshold] =
        useState(1000);
    const [
        manualReviewDaysOverdueThreshold,
        setManualReviewDaysOverdueThreshold,
    ] = useState(60);
    const [
        legalActionDaysOverdueThreshold,
        setLegalActionDaysOverdueThreshold,
    ] = useState(14);
    // ── Stage 2G — conversational auto-reply engine ──
    const [autoReply, setAutoReply] = useState<Required<IAutoReplySettings>>(
        () => normalizeAutoReply(),
    );
    // ── Stage 2I — behaviour-driven recommendations engine ──
    const [recommendations, setRecommendations] =
        useState<FullRecommendationSettings>(() => normalizeRecommendations());
    const [isSaving, setIsSaving] = useState(false);

    const canEdit = role === "owner" || role === "admin";

    // Settings is owner/admin only. Staff who reach this via a direct URL get
    // bounced back to the dashboard (the nav already hides the entry for them).
    const isStaff = role === "staff";
    useEffect(() => {
        if (isStaff) {
            navigate("/collect/dashboard", { replace: true });
        }
    }, [isStaff, navigate]);

    useEffect(() => {
        if (businessId) {
            dispatch(fetchActiveBusiness(undefined));
            dispatch(fetchQuickBooksStatus(undefined));
        }
    }, [businessId, dispatch]);

    // Hydrate local form state from latest business payload.
    useEffect(() => {
        if (!business) return;
        setName(business.name || "");
        setTimezone(business.timezone || "America/Chicago");
        const s = business.settings || {};
        setEmailEnabled(s.emailEnabled !== false);
        setSmsEnabled(s.smsEnabled === true);
        setAutoSendEnabled(s.autoSendEnabled !== false);
        setManualApprovalRequired(s.manualApprovalRequired === true);
        setDefaultAiTone(
            (s.defaultAiTone as "friendly" | "professional" | "firm") ||
            "professional",
        );
        setPromiseGraceDays(
            typeof s.promiseGraceDays === "number" ? s.promiseGraceDays : 1,
        );
        setBrokenPromiseThreshold(
            typeof s.brokenPromiseThreshold === "number"
                ? s.brokenPromiseThreshold
                : 2,
        );
        setEscalationDaysPerStage(
            typeof s.escalationDaysPerStage === "number"
                ? s.escalationDaysPerStage
                : 7,
        );
        setManualReviewBalanceThreshold(
            typeof s.manualReviewBalanceThreshold === "number"
                ? s.manualReviewBalanceThreshold
                : 1000,
        );
        setManualReviewDaysOverdueThreshold(
            typeof s.manualReviewDaysOverdueThreshold === "number"
                ? s.manualReviewDaysOverdueThreshold
                : 60,
        );
        setLegalActionDaysOverdueThreshold(
            typeof s.legalActionDaysOverdueThreshold === "number"
                ? s.legalActionDaysOverdueThreshold
                : 14,
        );
        setAutoReply(normalizeAutoReply(s.autoReply));
        setRecommendations(normalizeRecommendations(s.recommendations));
    }, [business]);

    const isDirty = useMemo(() => {
        if (!business) return false;
        const s = business.settings || {};
        return (
            name !== (business.name || "") ||
            timezone !== (business.timezone || "America/Chicago") ||
            emailEnabled !== (s.emailEnabled !== false) ||
            smsEnabled !== (s.smsEnabled === true) ||
            autoSendEnabled !== (s.autoSendEnabled !== false) ||
            manualApprovalRequired !== (s.manualApprovalRequired === true) ||
            defaultAiTone !==
            ((s.defaultAiTone as "friendly" | "professional" | "firm") ||
                "professional") ||
            promiseGraceDays !==
            (typeof s.promiseGraceDays === "number" ? s.promiseGraceDays : 1) ||
            brokenPromiseThreshold !==
            (typeof s.brokenPromiseThreshold === "number"
                ? s.brokenPromiseThreshold
                : 2) ||
            escalationDaysPerStage !==
            (typeof s.escalationDaysPerStage === "number"
                ? s.escalationDaysPerStage
                : 7) ||
            manualReviewBalanceThreshold !==
            (typeof s.manualReviewBalanceThreshold === "number"
                ? s.manualReviewBalanceThreshold
                : 1000) ||
            manualReviewDaysOverdueThreshold !==
            (typeof s.manualReviewDaysOverdueThreshold === "number"
                ? s.manualReviewDaysOverdueThreshold
                : 60) ||
            legalActionDaysOverdueThreshold !==
            (typeof s.legalActionDaysOverdueThreshold === "number"
                ? s.legalActionDaysOverdueThreshold
                : 14) ||
            JSON.stringify(normalizeAutoReply(autoReply)) !==
            JSON.stringify(normalizeAutoReply(s.autoReply)) ||
            JSON.stringify(normalizeRecommendations(recommendations)) !==
            JSON.stringify(normalizeRecommendations(s.recommendations))
        );
    }, [
        business,
        name,
        timezone,
        emailEnabled,
        smsEnabled,
        autoSendEnabled,
        manualApprovalRequired,
        defaultAiTone,
        promiseGraceDays,
        brokenPromiseThreshold,
        escalationDaysPerStage,
        manualReviewBalanceThreshold,
        manualReviewDaysOverdueThreshold,
        legalActionDaysOverdueThreshold,
        autoReply,
        recommendations,
    ]);

    const onSave = async () => {
        if (!businessId || !canEdit) return;
        setIsSaving(true);
        const result: any = await dispatch(
            updateBusinessSettings({
                name: name.trim() || undefined,
                timezone,
                emailEnabled,
                smsEnabled,
                autoSendEnabled,
                manualApprovalRequired,
                defaultAiTone,
                promiseGraceDays,
                brokenPromiseThreshold,
                escalationDaysPerStage,
                manualReviewBalanceThreshold,
                manualReviewDaysOverdueThreshold,
                legalActionDaysOverdueThreshold,
                autoReply,
                recommendations,
            }),
        );
        setIsSaving(false);
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Workspace settings saved.");
        } else {
            toast.error(result?.payload || "Couldn't save settings. Try again.");
        }
    };

    const qbStatusChip = (() => {
        if (!qb || qb.status === "disconnected") {
            return (
                <Chip
                    size="small"
                    icon={<ErrorOutline fontSize="small" />}
                    label="Not connected"
                    sx={{
                        backgroundColor: "rgba(107, 114, 128, 0.12)",
                        color: "#374151",
                        fontWeight: 600,
                    }}
                />
            );
        }
        if (qb.status === "active") {
            return (
                <Chip
                    size="small"
                    icon={<CheckCircleOutlined fontSize="small" />}
                    label={`Connected${qb.companyName ? ` — ${qb.companyName}` : ""}`}
                    sx={{
                        backgroundColor: "rgba(30, 193, 149, 0.14)",
                        color: "#0f7a5b",
                        fontWeight: 600,
                    }}
                />
            );
        }
        return (
            <Chip
                size="small"
                icon={<ErrorOutline fontSize="small" />}
                label={qb.status === "expired" ? "Token expired" : "Connection error"}
                sx={{
                    backgroundColor: "rgba(220, 38, 38, 0.12)",
                    color: "#b91c1c",
                    fontWeight: 600,
                }}
            />
        );
    })();

    // Staff are redirected by the effect above; render nothing meanwhile.
    if (isStaff) return null;

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "900px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 24 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Workspace settings
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                            }}
                        >
                            Control how reminders are sent for this workspace.
                        </Typography>
                    </Box>
                    <CollectPrimaryButton
                        onClick={onSave}
                        disabled={!isDirty || isSaving || !canEdit}
                        startIcon={
                            isSaving ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : undefined
                        }
                    >
                        {isSaving ? "Saving…" : "Save changes"}
                    </CollectPrimaryButton>
                </FlexBetween>

                {!canEdit && (
                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: "rgba(245, 158, 11, 0.1)",
                            color: "#92400e",
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.85rem",
                        }}
                    >
                        You have read-only access to this workspace. Ask an owner or
                        admin to change settings.
                    </Box>
                )}

                {/* ── Workspace ─────────────────────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="Workspace"
                        subtitle="Display name and timezone for cron scheduling."
                    />
                    <Stack spacing={2}>
                        <TextField
                            label="Workspace name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Timezone"
                            select
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            helperText="Dunning runs once a day at 9:00 AM in this timezone."
                        >
                            {TIMEZONES.map((tz) => (
                                <MenuItem key={tz.value} value={tz.value}>
                                    {tz.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </CollectSectionCard>

                {/* ── Reminders ─────────────────────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="Reminders"
                        subtitle="Channels and automation controls for the dunning engine."
                    />
                    <Stack spacing={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoSendEnabled}
                                    onChange={(e) => setAutoSendEnabled(e.target.checked)}
                                    disabled={!canEdit}
                                    color="success"
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Auto-send is on"
                                    hint="Master kill switch. Turn off to pause every scheduled reminder."
                                />
                            }
                        />
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={emailEnabled}
                                    onChange={(e) => setEmailEnabled(e.target.checked)}
                                    disabled={!canEdit}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Email reminders"
                                    hint="Send reminders via email through your configured provider."
                                />
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={smsEnabled}
                                    onChange={(e) => setSmsEnabled(e.target.checked)}
                                    disabled={!canEdit}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="SMS reminders"
                                    hint="Send reminders via text message. Provision a Twilio number below to enable."
                                />
                            }
                        />
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={manualApprovalRequired}
                                    onChange={(e) =>
                                        setManualApprovalRequired(e.target.checked)
                                    }
                                    disabled={!canEdit}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Require manual approval"
                                    hint="Queue reminders for review before they leave. Off by default."
                                />
                            }
                        />
                    </Stack>
                </CollectSectionCard>

                {/* ── SMS provisioning (Twilio) ────────────────────────── */}
                {businessId && (
                    <CollectSmsProvisioning
                        businessId={businessId}
                        canEdit={canEdit}
                    />
                )}

                {/* ── AI tone ──────────────────────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="Default AI tone"
                        subtitle="Used when AI rewrites or composes new reminders."
                    />
                    <TextField
                        select
                        value={defaultAiTone}
                        onChange={(e) =>
                            setDefaultAiTone(
                                e.target.value as
                                | "friendly"
                                | "professional"
                                | "firm",
                            )
                        }
                        disabled={!canEdit}
                        fullWidth
                        size="small"
                    >
                        {AI_TONES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        {t.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.75rem",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t.hint}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField>
                </CollectSectionCard>

                {/* ── Recovery & escalation policy ─────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="Recovery & escalation policy"
                        subtitle="Thresholds the escalation engine and promise watchdog use to advance overdue invoices and flag accounts for manual review."
                    />
                    <Stack spacing={2}>
                        <TextField
                            label="Escalation days per stage"
                            type="number"
                            value={escalationDaysPerStage}
                            onChange={(e) =>
                                setEscalationDaysPerStage(
                                    clampInt(e.target.value, 1, 60, 7),
                                )
                            }
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1, max: 60 }}
                            helperText="Days an invoice waits in a recovery stage before escalating to the next one (1–60)."
                        />
                        <TextField
                            label="Promise grace days"
                            type="number"
                            value={promiseGraceDays}
                            onChange={(e) =>
                                setPromiseGraceDays(clampInt(e.target.value, 0, 30, 1))
                            }
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            inputProps={{ min: 0, max: 30 }}
                            helperText="Days past a promised pay date before the promise is treated as broken (0–30)."
                        />
                        <TextField
                            label="Broken promise threshold"
                            type="number"
                            value={brokenPromiseThreshold}
                            onChange={(e) =>
                                setBrokenPromiseThreshold(
                                    clampInt(e.target.value, 1, 10, 2),
                                )
                            }
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1, max: 10 }}
                            helperText="Number of broken promises before an account is escalated for manual review (1–10)."
                        />
                        <Divider sx={{ my: 0.5 }} />
                        <TextField
                            label="Manual review balance threshold"
                            type="number"
                            value={manualReviewBalanceThreshold}
                            onChange={(e) =>
                                setManualReviewBalanceThreshold(
                                    clampInt(e.target.value, 0, 10_000_000, 1000),
                                )
                            }
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            inputProps={{ min: 0 }}
                            helperText="Outstanding balance (in your currency) that routes an account to manual review instead of auto-escalation."
                        />
                        <TextField
                            label="Manual review days overdue threshold"
                            type="number"
                            value={manualReviewDaysOverdueThreshold}
                            onChange={(e) =>
                                setManualReviewDaysOverdueThreshold(
                                    clampInt(e.target.value, 1, 365, 60),
                                )
                            }
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1, max: 365 }}
                            helperText="Days overdue after which an invoice is flagged for manual review (1–365)."
                        />
                        <TextField
                            label="Legal action days overdue threshold"
                            type="number"
                            value={legalActionDaysOverdueThreshold}
                            onChange={(e) =>
                                setLegalActionDaysOverdueThreshold(
                                    clampInt(e.target.value, 1, 365, 14),
                                )
                            }
                            disabled={!canEdit}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1, max: 365 }}
                            helperText="Days overdue after which an invoice is automatically escalated to legal action (1–365). Default 14."
                        />
                    </Stack>
                </CollectSectionCard>

                {/* ── Automatic replies (Stage 2G) ─────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="Automatic replies"
                        subtitle="When a customer replies to a reminder, the assistant can send one short, polite response — asking for a date when a promise is vague, confirming a promise when a date is given, or acknowledging a dispute. Rate-limited so customers are never spammed."
                    />
                    <Stack spacing={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoReply.enabled}
                                    onChange={(e) =>
                                        setAutoReply((p) => ({
                                            ...p,
                                            enabled: e.target.checked,
                                        }))
                                    }
                                    disabled={!canEdit}
                                    color="success"
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Enable automatic replies"
                                    hint="Master switch. When off, inbound replies are still classified and queued for review, just no auto-response is sent."
                                />
                            }
                        />
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoReply.askForDateOnVague}
                                    onChange={(e) =>
                                        setAutoReply((p) => ({
                                            ...p,
                                            askForDateOnVague: e.target.checked,
                                        }))
                                    }
                                    disabled={!canEdit || !autoReply.enabled}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Ask for a date on vague promises"
                                    hint="When a customer says they'll pay later without a date, reply asking for a specific day."
                                />
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoReply.confirmPromise}
                                    onChange={(e) =>
                                        setAutoReply((p) => ({
                                            ...p,
                                            confirmPromise: e.target.checked,
                                        }))
                                    }
                                    disabled={!canEdit || !autoReply.enabled}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Confirm promises to pay"
                                    hint="When a customer gives a concrete date, acknowledge it and let them know reminders are paused until then."
                                />
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoReply.ackDispute}
                                    onChange={(e) =>
                                        setAutoReply((p) => ({
                                            ...p,
                                            ackDispute: e.target.checked,
                                        }))
                                    }
                                    disabled={!canEdit || !autoReply.enabled}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Acknowledge disputes & complaints"
                                    hint="Send a brief, empathetic note that a person will review, then hand off to a human."
                                />
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoReply.ackAlreadyPaid}
                                    onChange={(e) =>
                                        setAutoReply((p) => ({
                                            ...p,
                                            ackAlreadyPaid: e.target.checked,
                                        }))
                                    }
                                    disabled={!canEdit || !autoReply.enabled}
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Acknowledge already-paid replies"
                                    hint="Reassure the customer the payment will be verified against your records."
                                />
                            }
                        />
                        <Divider sx={{ my: 1 }} />
                        <TextField
                            label="Max auto-replies per invoice"
                            type="number"
                            value={autoReply.maxPerThread}
                            onChange={(e) =>
                                setAutoReply((p) => ({
                                    ...p,
                                    maxPerThread: clampInt(e.target.value, 1, 10, 3),
                                }))
                            }
                            disabled={!canEdit || !autoReply.enabled}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1, max: 10 }}
                            helperText="Hard cap on automatic replies sent for a single invoice conversation (1–10)."
                        />
                        <TextField
                            label="Minimum hours between auto-replies"
                            type="number"
                            value={autoReply.minHoursBetween}
                            onChange={(e) =>
                                setAutoReply((p) => ({
                                    ...p,
                                    minHoursBetween: clampInt(e.target.value, 0, 168, 12),
                                }))
                            }
                            disabled={!canEdit || !autoReply.enabled}
                            fullWidth
                            size="small"
                            inputProps={{ min: 0, max: 168 }}
                            helperText="Quiet period before another automatic reply can be sent to the same customer (0–168)."
                        />
                    </Stack>
                </CollectSectionCard>

                {/* ── Recommendations (Stage 2I) ───────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="Recommendations"
                        subtitle="The assistant studies how each customer pays and suggests the next best step (e.g. reduce reminders for reliable payers, escalate delinquents). Auto-apply turns a suggestion into an internal task for your team automatically, it never messages the customer."
                    />
                    <Stack spacing={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={recommendations.enabled}
                                    onChange={(e) =>
                                        setRecommendations((p) => ({
                                            ...p,
                                            enabled: e.target.checked,
                                        }))
                                    }
                                    disabled={!canEdit}
                                    color="success"
                                />
                            }
                            label={
                                <SettingLabel
                                    title="Enable auto-apply"
                                    hint="Master switch. When off, recommendations are still generated and shown on the Recommendations page for you to review — nothing is applied automatically."
                                />
                            }
                        />
                        <Divider sx={{ my: 1 }} />
                        {RECOMMENDATION_AUTO_APPLY_OPTIONS.map((opt) => (
                            <FormControlLabel
                                key={opt.key}
                                control={
                                    <Switch
                                        checked={recommendations.autoApply[opt.key]}
                                        onChange={(e) =>
                                            setRecommendations((p) => ({
                                                ...p,
                                                autoApply: {
                                                    ...p.autoApply,
                                                    [opt.key]: e.target.checked,
                                                },
                                            }))
                                        }
                                        disabled={!canEdit || !recommendations.enabled}
                                    />
                                }
                                label={
                                    <SettingLabel
                                        title={opt.title}
                                        hint={opt.hint}
                                    />
                                }
                            />
                        ))}
                    </Stack>
                </CollectSectionCard>

                {/* ── QuickBooks ────────────────────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                    <SectionHeader
                        title="QuickBooks"
                        subtitle="Manage the connection to your QuickBooks Online company."
                    />
                    <FlexBetween>
                        {qbStatusChip}
                        <CollectGhostButton
                            startIcon={<LinkOutlined fontSize="small" />}
                            onClick={() => navigate("/collect/connect")}
                        >
                            Manage connection
                        </CollectGhostButton>
                    </FlexBetween>
                </CollectSectionCard>
            </Box>
        </CollectPageWrapper>
    );
};

// ── Small presentational helpers ────────────────────────────────────────

// Coerce a numeric text input to an integer clamped to [min, max]. Falls back
// to `fallback` when the field is cleared or non-numeric, so state never holds
// NaN (which would break the dirty-check and the save payload).
const clampInt = (
    raw: string,
    min: number,
    max: number,
    fallback: number,
): number => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return fallback;
    return Math.min(Math.max(n, min), max);
};

// Normalize the Stage 2G auto-reply block to a fully-populated shape so the
// dirty-check, hydration, and save payload all compare apples to apples.
const normalizeAutoReply = (
    ar?: IAutoReplySettings,
): Required<IAutoReplySettings> => ({
    enabled: ar?.enabled === true,
    maxPerThread: typeof ar?.maxPerThread === "number" ? ar.maxPerThread : 3,
    minHoursBetween:
        typeof ar?.minHoursBetween === "number" ? ar.minHoursBetween : 12,
    askForDateOnVague: ar?.askForDateOnVague !== false,
    confirmPromise: ar?.confirmPromise !== false,
    ackDispute: ar?.ackDispute !== false,
    ackAlreadyPaid: ar?.ackAlreadyPaid !== false,
});

// Fully-populated shape for the Stage 2I recommendations block. All auto-apply
// toggles default OFF (opt-in) so we never act on an account without consent.
type FullRecommendationSettings = {
    enabled: boolean;
    autoApply: {
        reduceOutreach: boolean;
        enableEarlyReminder: boolean;
        offerPaymentPlan: boolean;
        switchChannel: boolean;
        escalateReview: boolean;
        pauseAndReview: boolean;
    };
};

const normalizeRecommendations = (
    r?: IRecommendationSettings,
): FullRecommendationSettings => ({
    enabled: r?.enabled === true,
    autoApply: {
        reduceOutreach: r?.autoApply?.reduceOutreach === true,
        enableEarlyReminder: r?.autoApply?.enableEarlyReminder === true,
        offerPaymentPlan: r?.autoApply?.offerPaymentPlan === true,
        switchChannel: r?.autoApply?.switchChannel === true,
        escalateReview: r?.autoApply?.escalateReview === true,
        pauseAndReview: r?.autoApply?.pauseAndReview === true,
    },
});

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({
    title,
    subtitle,
}) => (
    <Box sx={{ mb: 2 }}>
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "1.05rem",
                color: colors.primary,
            }}
        >
            {title}
        </Typography>
        {subtitle && (
            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    color: "#6b7280",
                    fontSize: "0.82rem",
                    mt: 0.5,
                }}
            >
                {subtitle}
            </Typography>
        )}
    </Box>
);

const SettingLabel: React.FC<{ title: string; hint: string }> = ({
    title,
    hint,
}) => (
    <Box sx={{ ml: 1 }}>
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#1f2937",
            }}
        >
            {title}
        </Typography>
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                color: "#6b7280",
                fontSize: "0.75rem",
            }}
        >
            {hint}
        </Typography>
    </Box>
);

export default CollectSettings;
